/**
 * One intake template, open, taking the page. Built like a Requests draft:
 * the page bar (crumbs, save state, Preview, Use for a Client…), one column
 * with the name, description, the note every intake starts with and the
 * questions, and a rail with what it's for, what every new intake starts
 * with, the sections and the checks before it's used. Autosaves to the
 * template. Edits reach the next intake made from it; ones already sent keep
 * theirs. A CaseBlink starter opens read-only with Duplicate to Customize.
 */
import {
  Archive,
  Bell,
  CalendarClock,
  Check,
  Copy,
  Eye,
  FileText,
  Loader2,
  Lock,
  MoreHorizontal,
  Palette,
  RotateCcw,
  Send,
  Sparkles,
  UserRound,
  Video
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type FC
} from "react";
import { toast } from "sonner";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type {
  OutgoingIntake,
  OutgoingIntakeFormNode,
  OutgoingIntakeSavedTemplate
} from "@/api/types/outgoingIntake";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import { Tag } from "@/components/ui/tag";
import { Textarea } from "@/components/ui/textarea";
import { useOnScreen } from "@/features/agents/onScreen";
import { useTeamMembers } from "@/features/agents/useTeamMembers";
import {
  estimateLine,
  intakeEstimate
} from "@/features/foreignNationalOutgoingIntakes/intakeEstimate";
import {
  ChecksList,
  insertInSection,
  LibraryPicker,
  PageBar,
  QuestionsCard,
  RailLine,
  RailSection,
  SectionJumpList,
  useBuilderSelection
} from "@/features/intakeBuilder";
import { presendChecklist } from "@/features/requestsIde/editor/presend";
import { cadenceLabel } from "@/features/requestsIde/editor/reminders";
import { addNodes } from "@/features/requestsIde/editor/stepModel";
import {
  DEFAULT_EXPIRY_DAYS,
  DEFAULT_REMINDERS
} from "@/features/requestsIde/editor/useIntakeDraft";
import { LibraryDialog } from "@/features/requestsIde/editor/welcomeVideo/LibraryDialog";
import { RecordWelcomeVideoDialog } from "@/features/requestsIde/editor/welcomeVideo/RecordWelcomeVideoDialog";
import { ASSISTANT_NAME } from "@/features/requestsIde/model";
import { PreviewDialog } from "@/features/requestsIde/pages/PreviewDialog";

import type { IntakeTemplatesState } from "../hooks/useIntakeTemplates";
import { useTemplateDraft } from "../hooks/useTemplateDraft";
import { useTemplateLibrary } from "../hooks/useTemplateLibrary";
import { useTemplateWelcomeVideo } from "../hooks/useTemplateWelcomeVideo";
import {
  duplicateName,
  sentLabel,
  startsWithLabel,
  templateReminders
} from "../intakeTemplates";
import {
  NotePreview,
  TemplateDefaultsSheet,
  TemplateNoteField
} from "./TemplateDefaultsSheet";
import type { UsableTemplate } from "./UseTemplateDialog";
import { VisaPicker } from "./VisaPicker";

type Props = {
  templateId: string;
  templates: IntakeTemplatesState;
  canEdit: boolean;
  /** Firm Knowledge, the first crumb. */
  onHome: () => void;
  onBack: () => void;
  onOpen: (templateId: string) => void;
  onUse: (template: UsableTemplate, beforeSend?: () => Promise<void>) => void;
  onArchive: (template: OutgoingIntakeSavedTemplate, archived: boolean) => void;
  /** Ask Casey to revise this template. */
  onAskCasey?: (template: OutgoingIntakeSavedTemplate) => void;
};

/** What the client-facing preview reads, from a template. */
const previewIntake = (
  id: string,
  name: string,
  schema: OutgoingIntakeFormNode[],
  note: string | null
): OutgoingIntake =>
  ({
    outgoing_intake_id: `template:${id}`,
    title: name,
    schema,
    attorney_note: note,
    status: "draft"
  } as OutgoingIntake);

/** The checks that don't depend on who receives it. */
const RECIPIENT_CODES = new Set([
  "recipient",
  "missing_recipient",
  "invalid_recipient"
]);

const SaveState: FC<{
  state: ReturnType<typeof useTemplateDraft>["saveState"];
  onRetry: () => void;
}> = ({ state, onRetry }) =>
  state === "error" ? (
    <Button variant="link" size="xs" className="text-danger" onClick={onRetry}>
      Not Saved · Retry
    </Button>
  ) : (
    <span
      aria-live="polite"
      className="inline-flex shrink-0 items-center gap-1 text-sm whitespace-nowrap text-fg-3"
      data-testid="template-save-state"
    >
      {state === "saved" ? (
        <>
          <Check className="size-3" /> Saved
        </>
      ) : (
        <>
          <Loader2 className="size-3 animate-spin" /> Saving
        </>
      )}
    </span>
  );

type Fact = {
  icon: ComponentType<{ className?: string }>;
  text: string;
  muted?: boolean;
};

export const IntakeTemplateEditor: FC<Props> = ({
  templateId,
  templates,
  canEdit,
  onHome,
  onBack,
  onOpen,
  onUse,
  onArchive,
  onAskCasey
}) => {
  const saved = templates.savedById(templateId);
  const starter = saved ? null : templates.catalogById(templateId);
  const draft = useTemplateDraft(saved, templates.applySaved);
  const { members } = useTeamMembers();
  const library = useTemplateLibrary(templates.catalog);
  const [starterSchema, setStarterSchema] = useState<
    OutgoingIntakeFormNode[] | null
  >(null);
  const [preview, setPreview] = useState(false);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  // Load once per starter; the loader's identity follows the catalog.
  const loadStarter = useRef(templates.loadStarterSchema);
  loadStarter.current = templates.loadStarterSchema;
  const starterId = starter?.template_id ?? null;
  useEffect(() => {
    if (!starterId) return undefined;
    let cancelled = false;
    void loadStarter
      .current(starterId)
      .then((loaded) => !cancelled && setStarterSchema(loaded))
      .catch(() => !cancelled && setStarterSchema([]));
    return () => {
      cancelled = true;
    };
  }, [starterId]);

  const archived = Boolean(saved?.archived_at);
  const editable = Boolean(saved) && canEdit && !archived;
  const schema = saved ? draft.schema : starterSchema ?? [];
  const name = saved ? draft.name : starter?.label ?? "Template";
  const visa = saved ? draft.visa : starter?.visa ?? null;
  const selection = useBuilderSelection(schema);
  useOnScreen(
    saved || starter
      ? {
          kind: "template",
          id: templateId,
          label: name,
          ...(selection.openId
            ? { selected_node_ids: [selection.openId] }
            : {}),
          ...(saved && draft.saveState !== "saved" ? { dirty: true } : {})
        }
      : null
  );
  const video = useTemplateWelcomeVideo(
    saved,
    name,
    schema,
    templates.applySaved
  );
  const estimate = useMemo(() => intakeEstimate(schema), [schema]);
  const checks = useMemo(
    () =>
      presendChecklist(schema, "").filter((c) => !RECIPIENT_CODES.has(c.code)),
    [schema]
  );
  const { setSchema } = draft;

  const addForm = useCallback(
    async (formId: string) => {
      try {
        const nodes = await templates.loadStarterSchema(formId);
        const { schema: next, added } = addNodes(schema, nodes);
        setSchema(next);
        toast.success(
          added
            ? `Added ${added} question${added === 1 ? "" : "s"}`
            : "Every question was already in the template"
        );
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Couldn't add the form");
      }
    },
    [schema, setSchema, templates]
  );

  const duplicate = async () => {
    setDuplicating(true);
    try {
      const copy = await OutgoingIntakesAPI.createSavedTemplate({
        name: duplicateName({ name, kind: saved ? "firm" : "starter" }),
        schema,
        visa,
        description: saved
          ? draft.description || null
          : starter?.description ?? null,
        ...(saved ? draft.defaults : {})
      });
      templates.applySaved(copy);
      toast.success(`Duplicated to Firm Templates as “${copy.name}”`);
      onOpen(copy.template_id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't duplicate it");
    } finally {
      setDuplicating(false);
    }
  };

  if (!saved && !starter) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-base text-fg-3">
        {templates.loading ? (
          <Spinner className="size-4" />
        ) : (
          <>
            This template no longer exists.
            <Button variant="secondary" size="sm" onClick={onBack}>
              Back to Intake Templates
            </Button>
          </>
        )}
      </div>
    );
  }

  const loadingStarter = Boolean(starter) && starterSchema === null;
  const { defaults } = draft;
  const note = (saved ? defaults.attorney_note : null) ?? "";
  const reminders = cadenceLabel(
    templateReminders(defaults.reminder_schedule, DEFAULT_REMINDERS)
  );
  const facts: Fact[] = saved
    ? [
        {
          icon: FileText,
          text: note.trim() ? "A note to the client" : "No note",
          muted: !note.trim()
        },
        {
          icon: Video,
          text: video.upload
            ? `Uploading video, ${Math.round(video.upload.progress * 100)}%`
            : video.url
            ? "A welcome video"
            : "No welcome video",
          muted: !video.url && !video.upload
        },
        {
          icon: CalendarClock,
          text: `Link lasts ${
            defaults.expires_in_days ?? DEFAULT_EXPIRY_DAYS
          } days`
        },
        {
          icon: Bell,
          text:
            reminders === "Off" ? "No reminders" : `Reminders: ${reminders}`,
          muted: reminders === "Off"
        },
        {
          icon: Palette,
          text: defaults.enable_firm_branding
            ? "Firm branding"
            : "CaseBlink branding",
          muted: true
        }
      ]
    : [];
  const ownerId = saved?.updated_by_user_id ?? saved?.created_by_user_id;
  const owner = members.find((m) => m.uid === ownerId)?.label ?? null;
  const flushThen = (fn: () => void) => async () => {
    await draft.flush();
    fn();
  };
  const usable: UsableTemplate = {
    id: saved?.template_id ?? starter?.template_id ?? templateId,
    name,
    schema: loadingStarter ? null : schema,
    defaults: saved
      ? {
          ...defaults,
          welcome_video_url: saved.welcome_video_url ?? null
        }
      : null
  };

  return (
    <div
      className="flex h-full min-h-0 flex-col bg-surface"
      data-testid="intake-template-editor"
    >
      <PageBar
        crumbs={[
          { label: "Firm Knowledge", onClick: onHome },
          {
            label: "Intake Templates",
            onClick: () => {
              void draft.flush();
              onBack();
            }
          }
        ]}
        title={name || "Untitled Template"}
        meta={
          <>
            {starter ? <Tag>CaseBlink Starter</Tag> : null}
            {archived ? <Tag>Archived</Tag> : null}
            {editable ? (
              <SaveState
                state={draft.saveState}
                onRetry={() => void draft.flush()}
              />
            ) : null}
          </>
        }
        actions={
          <>
            {editable && onAskCasey && saved ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void flushThen(() => onAskCasey(saved))()}
              >
                <Sparkles /> Ask {ASSISTANT_NAME}
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              disabled={loadingStarter}
              onClick={() => setPreview(true)}
              data-testid="template-preview"
            >
              <Eye /> Preview
            </Button>
            {saved && canEdit ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon-sm" aria-label="More">
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem
                    disabled={duplicating}
                    onSelect={() => void duplicate()}
                  >
                    <Copy /> Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => onArchive(saved, !archived)}
                  >
                    {archived ? <RotateCcw /> : <Archive />}
                    {archived ? "Restore" : "Archive"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            {saved ? (
              <Button
                variant={archived ? "secondary" : "primary"}
                size="sm"
                disabled={archived || estimate.questions === 0}
                onClick={() => onUse(usable, draft.flush)}
                data-testid="template-use"
              >
                <Send /> Use for a Client…
              </Button>
            ) : (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={loadingStarter || estimate.questions === 0}
                  onClick={() => onUse(usable)}
                  data-testid="template-use"
                >
                  <Send /> Use for a Client…
                </Button>
                {canEdit ? (
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={loadingStarter || duplicating}
                    onClick={() => void duplicate()}
                    data-testid="template-duplicate"
                  >
                    <Copy /> Duplicate to Customize
                  </Button>
                ) : null}
              </>
            )}
          </>
        }
      />

      {!saved || archived ? (
        <div
          className="flex h-9 shrink-0 items-center gap-2 border-b border-line-1 bg-fill-1 px-4 text-sm text-fg-2"
          data-testid="template-read-only"
        >
          <Lock className="size-3.5 text-fg-3" aria-hidden />
          {archived
            ? "Archived. It's out of every picker; restore it to change or use it."
            : "A CaseBlink starter. It stays read-only so CaseBlink can keep it current with USCIS; duplicate it to change questions."}
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-y-auto">
          <div className="mx-auto flex max-w-180 flex-col gap-5 px-6 py-6">
            <div className="flex flex-col gap-1">
              {editable ? (
                <input
                  value={draft.name}
                  onChange={(e) => draft.setName(e.target.value)}
                  placeholder="Untitled template"
                  aria-label="Template Name"
                  data-testid="template-name"
                  className="min-w-0 bg-transparent text-xl font-medium text-fg outline-none placeholder:text-fg-4"
                />
              ) : (
                <h1 className="text-xl font-medium text-fg">{name}</h1>
              )}
              {editable ? (
                <Textarea
                  value={draft.description}
                  rows={1}
                  maxLength={500}
                  aria-label="Description"
                  placeholder="One line on what it covers, for whoever picks it next."
                  onChange={(e) => draft.setDescription(e.target.value)}
                  className="min-h-0 resize-none border-0 bg-transparent px-0 text-base text-fg-2 shadow-none focus-visible:ring-0"
                />
              ) : (
                <span className="text-base text-fg-2">
                  {(saved ? draft.description : starter?.description) ||
                    "No description."}
                </span>
              )}
              <span className="text-sm text-fg-3">
                {estimateLine(estimate)}
              </span>
            </div>

            {saved ? (
              editable ? (
                <TemplateNoteField
                  id="template-note"
                  value={note}
                  onChange={(next) =>
                    draft.setDefaults({ attorney_note: next || null })
                  }
                  rows={2}
                />
              ) : note ? (
                <NotePreview note={note} />
              ) : null
            ) : null}

            {loadingStarter ? (
              <div className="flex justify-center py-10">
                <Spinner className="size-4" />
              </div>
            ) : (
              <QuestionsCard
                schema={schema}
                onChange={setSchema}
                selection={selection}
                who="The client"
                readOnly={!editable}
                renderLibrary={
                  editable
                    ? (section) => (
                        <LibraryPicker
                          source={library}
                          onAddQuestion={(node) => {
                            const next = insertInSection(schema, section, node);
                            if (!next.added) {
                              toast.message(
                                "That question is already in the template"
                              );
                              return;
                            }
                            setSchema(next.schema);
                            selection.setOpenId(node.node_id);
                          }}
                          onAddForm={(id) => void addForm(id)}
                        />
                      )
                    : undefined
                }
                emptyHint={`Add a section to start, or ask ${ASSISTANT_NAME} to draft it.`}
              />
            )}
          </div>
        </div>

        <aside
          className="flex w-64 shrink-0 flex-col gap-6 overflow-y-auto border-l border-line-1 px-4 py-5"
          aria-label="Template Details"
        >
          <RailSection title="About">
            <div className="flex flex-col gap-2">
              {editable ? (
                <VisaPicker
                  id="template-visa"
                  value={draft.visa ? [draft.visa] : []}
                  onChange={(next) => draft.setVisa(next[0] ?? null)}
                  placeholder="Any visa"
                />
              ) : (
                <Tag className="self-start">{visa || "Any Visa"}</Tag>
              )}
              {saved ? (
                <>
                  <RailLine icon={UserRound}>{owner ?? "Your firm"}</RailLine>
                  <RailLine icon={Send}>
                    {sentLabel(saved.sent_count ?? 0)}
                  </RailLine>
                  <span className="text-sm text-fg-3">
                    Edits reach the next intake made from it; sent ones keep
                    their questions.
                  </span>
                </>
              ) : (
                <span className="text-sm text-fg-3">
                  Kept current by CaseBlink. Duplicate it to change questions.
                </span>
              )}
            </div>
          </RailSection>
          {saved ? (
            <RailSection
              title="Every New Intake Starts With"
              action={
                editable ? (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setDefaultsOpen(true)}
                    data-testid="template-defaults-edit"
                  >
                    Edit
                  </Button>
                ) : undefined
              }
            >
              <div
                className="flex flex-col gap-1.5"
                data-testid="template-starts-with"
              >
                {facts.map((f) => (
                  <RailLine
                    key={f.text}
                    icon={f.icon}
                    tone={f.muted ? "muted" : "default"}
                  >
                    {f.text}
                  </RailLine>
                ))}
              </div>
            </RailSection>
          ) : null}
          <RailSection title="Sections">
            <SectionJumpList
              schema={schema}
              selection={selection}
              readOnly={!editable}
            />
          </RailSection>
          <RailSection title="Before It's Used">
            <ChecksList items={checks} onJump={selection.jumpTo} compact />
          </RailSection>
        </aside>
      </div>

      {saved ? (
        <>
          <TemplateDefaultsSheet
            open={defaultsOpen}
            onOpenChange={setDefaultsOpen}
            defaults={defaults}
            onChange={draft.setDefaults}
            video={video}
            readOnly={!editable}
            onLibrary={() => video.setLibrary(true)}
          />
          <LibraryDialog
            open={video.library}
            onOpenChange={video.setLibrary}
            scopes={["firm"]}
            currentId={video.videoId}
            onPick={(picked) => video.pickLibraryVideo(picked.video_id)}
          />
          <RecordWelcomeVideoDialog
            open={video.recording}
            onOpenChange={video.setRecording}
            mode="template"
            clientFirstName=""
            title={name}
            note={note}
            talkingPoints={video.talkingPoints}
            onUse={video.uploadFile}
            onUploadInstead={() => setDefaultsOpen(true)}
            onAskCasey={video.askCasey}
          />
        </>
      ) : null}
      <PreviewDialog
        open={preview}
        onOpenChange={setPreview}
        intake={previewIntake(
          templateId,
          name,
          schema,
          note ? note.replaceAll("{{first_name}}", "Ada") : null
        )}
        schema={schema}
        clientName="Ada"
      />
    </div>
  );
};
